package games.polyclub.website.frontend.store.components

import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classSet
import games.polyclub.frontend.lib.react.utils.useEffectOnceAsync
import games.polyclub.service.payment.api.id.OfferId
import games.polyclub.service.payment.api.id.ProductId
import games.polyclub.service.player.id.api.PlayerId
import games.polyclub.service.store.api.NameAndId
import games.polyclub.service.store.api.Offer
import games.polyclub.service.store.api.Product
import games.polyclub.service.store.api.id.ProductTag
import games.polyclub.tool.functional.Option
import games.polyclub.tool.functional.repeatingForever
import games.polyclub.tool.functional.retrying
import games.polyclub.tool.strings.Money.Companion.sumOfMoney
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.context.CartAddData
import games.polyclub.website.frontend.store.context.CartAddData.Who
import games.polyclub.website.frontend.store.context.CartAddData.Who.*
import games.polyclub.website.frontend.store.context.useCart
import games.polyclub.website.frontend.store.hooks.*
import kotlinx.coroutines.delay
import react.*
import react.dom.events.ChangeEventHandler
import react.dom.events.MouseEventHandler
import web.html.ButtonType
import web.html.HTMLButtonElement
import web.html.HTMLInputElement
import web.html.InputType
import kotlin.time.Duration.Companion.seconds

@Suppress("INLINE_CLASS_IN_EXTERNAL_DECLARATION_WARNING")
external interface ProductPurchaseProps: Props {
    var offerId: OfferId
    var mainProduct: ProductId
    var title: ReactElement<*>?
    var shortDescription: ReactElement<*>?
}

val ProductPurchaseForm = SFC<ProductPurchaseProps> { props ->
    val cartCtx = useCart()
    val login = useAuth()

    val (toWho, setToWho) = useState(ME)
    val (minecraftNick, setMinecraftNick) = useState("")
    val (minecraftId, setMinecraftId) = useState<Option<PlayerId>?>(null)
    val (offer, setOffer) = useState<Option<Offer.Loaded>?>(null)

    val toWhoOnChange = useCallback<ChangeEventHandler<HTMLInputElement>>(setToWho) { ev ->
        setToWho(Who.valueOf(ev.currentTarget.value))
    }

    val isNickReadOnly = toWho == ME && (login.auth?.profile?.currentName?.name ?: "").isNotBlank()
    val onValidationChange = useCallback(minecraftNick, setMinecraftId) { nick: String, valid: Boolean?, id: PlayerId? ->
        if (nick == minecraftNick) {
            val newValue = when (valid) {
                true -> Option.of(id)
                false -> Option.empty()
                null -> null
            }
            if (newValue != minecraftId) {
                setMinecraftId(newValue)
            }
        }
    }

    val onNickChange = useCallback(isNickReadOnly, minecraftNick, setMinecraftNick) { nick: String ->
        if (!isNickReadOnly && minecraftNick != nick) {
            setMinecraftNick(nick)
        }
    }

    val onSubmit = useCallback<MouseEventHandler<HTMLButtonElement>>(cartCtx, toWho, minecraftId, minecraftNick, offer) {
        if (offer != null && cartCtx.addToCart(CartAddData(
                toWho = toWho,
                to = minecraftId,
                offerId = offer.get().id,
                quantity = 1,
            ), true)
        ) {
            logEvent(
                category = GACategory.CART,
                action = GAAction.ADD_TO_CART,
                params = offer.get().toGA4(cartCtx.getCurrency(), login.auth?.profile)
            )
            if (toWho == ME) {
                cartCtx.setSelfIdentification(NameAndId(
                    name = minecraftNick,
                    id = minecraftId?.getOrNull()
                ))
            }
        }
    }

    appScope.useEffectOnceAsync {
        var sendReport = true
        repeatingForever {
            setOffer(retrying {
                Option.of(StoreBff.loadOffer(props.offerId))
            }.ifPresent { offer ->
                if (sendReport) {
                    sendReport = false
                    logEvent(
                        category = GACategory.CART,
                        action = GAAction.VIEW_PROMOTION,
                        params = offer.toGA4(cartCtx.getCurrency(), login.auth?.profile)
                    )
                }
            })
            delay(5.seconds)
        }
    }

    useEffect(login, login.auth?.profile) {
        val nick = login.auth?.profile?.currentName?.name
            ?: (cartCtx.selfIdentification?.name ?: "").takeIf { it.isNotEmpty() }
        if (nick != null) {
            setToWho(ME)
        }
    }

    useEffect(toWho, minecraftNick, setMinecraftNick, login) {
        when(toWho) {
            ME -> (login.auth?.profile?.currentName?.name ?: "").takeIf { it.isNotEmpty() }?.let { authNick ->
                if (authNick != minecraftNick) {
                    setMinecraftNick(authNick)
                }
            }
            GIFT -> if (login.auth?.profile?.currentName?.name == minecraftNick) {
                setMinecraftNick("")
            }
            GIVEAWAY -> Unit
        }
    }

    div("col-12 col-sm-6") {
        if (offer == null) {
            Loading()
            return@div
        }
        if (offer.isEmpty || !offer.get().isEnabled) {
            p {
                +"Oferta expirada"
            }
            return@div
        }
        val product = offer.get().products.firstOrNull { it.id == props.mainProduct } ?: offer.get().products.first()
        if (props.title != null) {
            +props.title
        } else {
            h3("my-3") {
                +product.name
            }
        }
        if (props.shortDescription != null) {
            +props.shortDescription
        } else {
            p {
                +product.description
            }
        }
        h4("mt-3") {
            +"Para quem?"
        }
        div("btn-group btn-group-toggle") {
            label("btn btn-default text-center") {
                if (toWho == ME) {
                    classSet += "btn-primary"
                    classSet += "active"
                } else classSet += "btn-default"
                input {
                    type = InputType.radio
                    name = "aofpage_for_radio"
                    value = ME.name
                    checked = toWho == ME
                    onChange = toWhoOnChange
                }
                +"Para mim"
            }
            label("btn btn-default text-center") {
                if (toWho == GIFT) {
                    classSet += "btn-primary"
                    classSet += "active"
                } else classSet += "btn-default"
                input {
                    type = InputType.radio
                    name = "aofpage_for_radio"
                    value = GIFT.name
                    checked = toWho == GIFT
                    onChange = toWhoOnChange
                }
                +"Para presente"
            }
            label("btn btn-default text-center") {
                if (toWho == GIVEAWAY) {
                    classSet += "btn-primary"
                    classSet += "active"
                } else classSet += "btn-default"
                input {
                    type = InputType.radio
                    name = "aofpage_for_radio"
                    value = GIVEAWAY.name
                    checked = toWho == GIVEAWAY
                    onChange = toWhoOnChange
                }
                +"Sorteio pela equipe (giveaway)"
            }
        }
        if (toWho == ME || toWho == GIFT) {
            MinecraftInput("mt-1") {
                this.placeholder = if (toWho == ME) "SeuApelidoAqui" else "ApelidoNoJogo"
                this.value = minecraftNick
                this.readOnly = isNickReadOnly
                this.onValidationChange = onValidationChange
                this.onChange = onNickChange
            }
        }
        if (product.type == Product.Type.TEMPORAL) {
            h4("mt-3") {
                +"Duração"
            }
            input("form-control") {
                type = InputType.text
                readOnly = true
                value = "30 dias corridos após o primeiro acesso${if (product.tags.contains(ProductTag("beta"))) " (depois da fase beta)" else ""}"
            }
        }
        div("bg-gray py-2 px-3 mt-4") {
            val currency = cartCtx.getCurrency()
            val originalPrice = currency.amount(product.price.getValue(currency))
            val validDiscounts = offer.get().discounts.filter { it.canBeAppliedTo(product.tags, originalPrice, login.auth?.profile) }
            val discountNames = validDiscounts.associate { it.id to it.name }
            val discounts = validDiscounts.map { it.applyTo(currency.amount(product.price)) }
            val reduction = discounts.sumOfMoney(currency) { it.reduction }
            val finalPrice = originalPrice - reduction
            h2("mb-0") {
                +finalPrice.toString()
                if (discounts.isNotEmpty()) {
                    small {
                        if (discounts.size == 1) {
                            +" (Desconto: ${discountNames.getValue(discounts.single().id)})"
                        } else {
                            +" (Descontos: ${discounts.joinToString { discountNames.getValue(it.id) }})"
                        }
                    }
                }
            }
            if (finalPrice.amount < originalPrice.amount) {
                h4 {
                    del {
                        +originalPrice.toString()
                    }
                }
            }
        }
        div("mt-4") {
            button("btn btn-primary btn-lg btn-flat") {
                type = ButtonType.button
                val stock = product.stock
                disabled = stock != null && stock <= 0
                i("fas fa-cart-plus fa-lg mr-2")
                +"Adicionar ao Carrinho"
                if (stock != null) {
                    small {
                        when {
                            stock == 1 -> +" (Último Disponível)"
                            stock > 0 -> +" (Restam: $stock)"
                            else -> +" (Esgotado)"
                        }
                    }
                }
                onClick = onSubmit
            }
            +" "
            /*div("btn btn-default btn-lg btn-flat") {
                i("fas fa-heart fa-lg mr-2")
                +"Adicionar na Lista de Desejos"
            }*/
        }
    }
}
